import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  ListInventorySnapshotsInput,
  ListInventorySnapshotsResponse,
  InventorySnapshotRow,
  GetProductInventoryDetailInput,
  GetProductInventoryDetailResponse,
  ProductWarehouseSnapshot,
  ProductInventoryLot,
} from '@erp/rpc';

export type {
  InventorySnapshotRow,
  ListInventorySnapshotsResponse,
  GetProductInventoryDetailResponse,
  ProductWarehouseSnapshot,
  ProductInventoryLot,
};

export function useInventorySnapshots(
  input: ListInventorySnapshotsInput,
  enabled = true,
) {
  return useQuery<ListInventorySnapshotsResponse>({
    ...orpc.procurement.inventorySnapshots.list.queryOptions({ input }),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useInfiniteInventorySnapshots(
  input: Omit<ListInventorySnapshotsInput, 'page'>,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: ['procurement', 'inventorySnapshots', 'infinite', input],
    queryFn: ({ pageParam }) =>
      rpcClient.procurement.inventorySnapshots.list({
        ...input,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      pages.reduce((count, page) => count + page.rows.length, 0) <
      lastPage.totalRows
        ? pages.length + 1
        : undefined,
    enabled,
  });
}

export function useProductInventoryDetail(
  input: GetProductInventoryDetailInput,
  enabled = true,
) {
  return useQuery<GetProductInventoryDetailResponse>({
    ...orpc.procurement.inventorySnapshots.getByProduct.queryOptions({ input }),
    enabled: enabled && !!input.productV3Key,
  });
}
